import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"

import { weekDaysObject } from "../constants/activitiesConstants"

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * La grille de créneaux proposée aux familles sur les offres régulières.
 *
 * Extraite de {@link ConstraintsGridStep} parce qu'elle a désormais trois
 * lecteurs : l'écran qui la dessine, l'amorçage de la sélection
 * (`canonicalSlotsToSelectableGridSlots`, qui doit écarter ce qu'elle ne
 * contient pas), et la conversion d'affichage ci-dessous.
 *
 * Fichier sans import React Native, pour rester consommable depuis les utils
 * et leurs tests. Il vit sous `src/data` pour que les ports de l'espace parent
 * le lisent aussi (ils n'ont pas le droit de lire `src/common`) : l'ancien
 * chemin `common/student-diagnostic-stepper/constraintsGrid` le ré-exporte.
 *
 * ⚠️ Ces heures sont celles de PARIS. C'est l'offre, pas une horloge locale :
 * un créneau nomme l'heure à laquelle la séance a lieu côté Masteur, la même
 * pour tout le monde. Le back en dépend —
 * {@see \App\Services\AvailabilityGridService::base()} en est le miroir
 * canonique et refuse tout ce qui n'y figure pas. Elles sont TRADUITES à
 * l'affichage pour une famille dans un autre fuseau ({@link localizeGridTime}),
 * jamais à l'envoi.
 */

/**
 * La grille, dans la forme canonique du serveur — la même liste que
 * `config/availability.php`, mot pour mot.
 *
 * À plat et en littéraux, sans constante partagée ni spread, pour se relire
 * ligne par ligne face au config PHP — et pour être lisible depuis un test PHP.
 * Une dérive entre les deux listes ne produit pas un écart cosmétique mais un
 * 422 sur une case que le parent vient de cocher, sans aucun geste pour s'en
 * sortir : le contrat est donc figé côté serveur
 * ({@see \Tests\Unit\Availability\BaseAvailabilityGridContractTest}, qui lit ce
 * fichier quand le monorepo complet est du checkout).
 */
const WEEKDAYS = [
    "Monday 17:00-18:00",
    "Monday 18:00-19:00",
    "Monday 19:00-20:00",
    "Tuesday 17:00-18:00",
    "Tuesday 18:00-19:00",
    "Tuesday 19:00-20:00",
    "Wednesday 14:00-15:00",
    "Wednesday 15:00-16:00",
    "Wednesday 16:00-17:00",
    "Wednesday 17:00-18:00",
    "Wednesday 18:00-19:00",
    "Wednesday 19:00-20:00",
    "Thursday 17:00-18:00",
    "Thursday 18:00-19:00",
    "Thursday 19:00-20:00",
    "Friday 17:00-18:00",
    "Friday 18:00-19:00",
    "Friday 19:00-20:00",
    "Saturday 09:00-10:00",
    "Saturday 10:00-11:00",
    "Saturday 11:00-12:00",
    "Saturday 14:00-15:00",
    "Saturday 15:00-16:00",
]

const SUNDAY = [
    "Sunday 09:00-10:00",
    "Sunday 10:00-11:00",
    "Sunday 11:00-12:00",
    "Sunday 14:00-15:00",
    "Sunday 15:00-16:00",
]

const DAY_LABELS = {
    Monday: "Lundi",
    Tuesday: "Mardi",
    Wednesday: "Mercredi",
    Thursday: "Jeudi",
    Friday: "Vendredi",
    Saturday: "Samedi",
    Sunday: "Dimanche",
}

/**
 * Les jours de la grille, avec leur libellé et leurs horaires — regroupés
 * depuis la liste canonique, dans son ordre.
 *
 * Le dimanche n'est ouvert qu'en cours particulier : en partagé, un groupe ne
 * se staffe pas le dimanche.
 */
export const constraintsGridDays = ({ includeSunday = false } = {}) => {
    const byDay = new Map()

    for (const slot of includeSunday ? [...WEEKDAYS, ...SUNDAY] : WEEKDAYS) {
        const parts = /^(\w+) (\d{2}):00-(\d{2}):00$/.exec(slot)
        if (!parts) continue

        const [, day, from, to] = parts
        if (!byDay.has(day)) {
            byDay.set(day, { day, label: DAY_LABELS[day] ?? day, times: [] })
        }
        byDay.get(day).times.push(`${from}h-${to}h`)
    }

    return Array.from(byDay.values())
}

/** La même grille à plat : ["Monday 17h-18h", …], en heures de Paris. */
export const constraintsGridSlots = ({ includeSunday = false } = {}) =>
    constraintsGridDays({ includeSunday }).flatMap((d) =>
        d.times.map((t) => `${d.day} ${t}`)
    )

const MINUTES_PER_DAY = 24 * 60

/** Jours abrégés, pour un créneau qui bascule la veille ou le lendemain. */
const SHORT_DAYS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."]
const DAY_INDEX = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
}

/**
 * Le fuseau de l'APPAREIL — le repli quand le serveur n'en connaît aucun.
 *
 * `users.timezone` n'est écrit que pour un compte connecté (l'appareil, ou l'IP
 * qui remplit un fuseau vide au login), qu'un lead du funnel d'inscription n'a
 * jamais : la config servait donc
 * `user_timezone: null`, et la grille s'affichait en heures de Paris à un lead
 * de Tunis — l'écart même que la traduction existe pour absorber. Le fuseau
 * connu du serveur reste prioritaire là où il existe (portail), pour que les
 * deux surfaces disent la même heure.
 *
 * Purement d'AFFICHAGE : la valeur cochée reste la chaîne Paris. Rend null
 * quand l'environnement ne résout pas les fuseaux (Intl sans ICU complet), et
 * l'affichage retombe alors sur les heures de Paris.
 */
export const deviceTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null
    } catch (e) {
        return null
    }
}

/**
 * Le fuseau à afficher pour le funnel d'inscription : celui que le serveur a
 * résolu pour CE lead, sinon celui de l'appareil qui lit l'écran EN CE MOMENT.
 *
 * Résolu ici, au point de lecture, plutôt que dans le reducer : un appareil
 * déjà venu peut porter un état persisté écrit par un build antérieur à ces
 * clés (`redux-persist` remplace la tranche `inscriptionFunnel` ENTIÈRE —
 * `autoMergeLevel2` ne fusionne que jusqu'au niveau de `data`, pas plus profond
 * — donc une tranche persistée sans `userTimezone` l'efface plutôt que de le
 * laisser à sa valeur par défaut). Un appel à `deviceTimezone()` ici, à chaque
 * lecture, ne dépend d'aucun état persisté et couvre cet appareil comme les
 * suivants.
 */
export const resolveFunnelUserTimezone = (userTimezone) =>
    userTimezone ?? deviceTimezone()

/**
 * Décalage, en heures, entre l'horloge du lecteur et celle du serveur.
 * Fractionnaire : une poignée de fuseaux sont à la demi-heure (Inde, Téhéran,
 * Adélaïde) ou au quart d'heure (Katmandou).
 *
 * Rend 0 dès qu'un des deux fuseaux manque, qu'ils coïncident, ou que
 * l'environnement ne sait pas les résoudre : l'affichage retombe alors sur les
 * heures de Paris telles quelles, ce qui est l'ancien comportement. Un
 * affichage qui échoue ne doit pas vider la grille.
 *
 * Calculé à une DATE, parce que l'écart n'est pas constant : Paris et Londres
 * sont à une heure d'écart toute l'année, mais Paris et Alger passent de 1 h à
 * 2 h selon l'heure d'été.
 */
export const gridDisplayOffsetHours = (
    userTimezone,
    serverTimezone,
    at = undefined
) => {
    if (!userTimezone || !serverTimezone || userTimezone === serverTimezone) {
        return 0
    }

    try {
        const when = at ? dayjs(at) : dayjs()
        const minutes =
            when.tz(userTimezone).utcOffset() -
            when.tz(serverTimezone).utcOffset()

        // Une date illisible rend `NaN` sans lever, donc le `catch` ne la voit
        // pas : non bornée, elle ferait annoncer « heure locale » au-dessus
        // d'une grille restée en heures de Paris.
        return Number.isFinite(minutes) ? minutes / 60 : 0
    } catch (e) {
        return 0
    }
}

/**
 * Un horaire de la grille (« 17h-18h », heure de Paris) → l'heure qu'il est
 * chez le lecteur, plus le décalage de jour que la conversion provoque.
 *
 * `dayShift` vaut 0 dans l'immense majorité des cas, mais pas tous : la grille
 * du samedi matin vue depuis les Amériques tombe la veille au soir, et une
 * case affichée sans le dire enverrait la famille au mauvais jour.
 *
 * @returns {{label: string, dayShift: number}}
 */
export const localizeGridTime = (time, offsetHours = 0) => {
    const parts = /^(\d{1,2})h-(\d{1,2})h$/.exec(String(time))
    if (!offsetHours || !parts) {
        return { label: String(time), dayShift: 0 }
    }

    // Arithmétique en MINUTES : l'écart n'est pas toujours un nombre entier
    // d'heures, et arrondir affichait une demi-heure de travers à l'Inde.
    const offsetMinutes = Math.round(offsetHours * 60)
    const start = Number(parts[1]) * 60 + offsetMinutes
    const end = Number(parts[2]) * 60 + offsetMinutes

    const label = (minutes) => {
        const inDay =
            ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
        const hour = String(Math.floor(inDay / 60)).padStart(2, "0")
        const minute = inDay % 60

        return minute === 0
            ? `${hour}h`
            : `${hour}h${String(minute).padStart(2, "0")}`
    }

    return {
        label: `${label(start)}-${label(end)}`,
        dayShift: Math.floor(start / MINUTES_PER_DAY),
    }
}

/** Le jour abrégé d'un créneau décalé — « ven. » sous la colonne « Samedi ». */
export const shiftedDayLabel = (weekday, dayShift) => {
    const index = DAY_INDEX[weekday]
    if (!dayShift || index === undefined) return null

    return SHORT_DAYS[(((index + dayShift) % 7) + 7) % 7]
}

/**
 * Créneau → libellé lisible : "Monday 17h-18h" → "Lundi 17h-18h".
 *
 * Les créneaux circulent avec un jour ANGLAIS (c'est la clé technique que la
 * grille émet et que l'attribution consomme) ; seul l'affichage est traduit,
 * via la table déjà utilisée ailleurs dans l'app. Un créneau non reconnu est
 * rendu tel quel plutôt que masqué — mieux vaut un libellé brut qu'un trou.
 */
const formatSlotLabel = (slot, offsetHours = 0) => {
    const [day, ...rest] = String(slot).split(" ")
    const label = weekDaysObject[day]
    if (!label) return String(slot)

    const local = localizeGridTime(rest.join(" "), offsetHours)
    const shifted = shiftedDayLabel(day, local.dayShift)

    return `${shifted ?? label} ${local.label}`
}

/**
 * Liste de créneaux → "Lundi 17h-18h · Jeudi 18h-19h".
 *
 * `offsetHours` traduit l'affichage à l'heure de la famille, comme la grille
 * elle-même ({@see localizeGridTime}) : les deux surfaces se lisent côte à côte
 * sur l'écran de reprise, et une ligne de résumé qui n'aurait pas suivi
 * afficherait une heure différente de la case cochée juste au-dessus. Par
 * défaut 0 pour l'appelant dont aucun fuseau n'est connu (visiteur anonyme du
 * lien signé) : il lit alors les heures de Paris, celles-là mêmes qui partent au
 * serveur.
 *
 * La mention « (heure locale) » se pose ICI, pas chez l'appelant : ces listes
 * s'affichent à un bouton d'écart les unes des autres (carte de reprise,
 * résumé du composeur), et chacune décidant pour elle-même, l'une l'annonçait
 * quand l'autre traduisait en silence.
 *
 * @param {string[]} [slots]
 * @param {number} [offsetHours]
 * @returns {string}
 */
export const formatSlotsLabel = (slots = [], offsetHours = 0) => {
    const label = slots
        .map((slot) => formatSlotLabel(slot, offsetHours))
        .join(" · ")

    return label && offsetHours ? `${label} (heure locale)` : label
}

/**
 * Créneaux CANONIQUES serveur → forme GRILLE heure par heure.
 *
 * Le back stocke les créneaux du jalon (`resume_slots`) dans la forme que
 * `Helper::slotsPossibleFromLocal` écrit — « Monday 17-19 » — quand la grille
 * du portail (`ConstraintsGridStep`) parle en heures simples « Monday
 * 17h-18h ». Cette conversion amorce la sélection de la modale de créneaux sur
 * l'existant, pour qu'une modification parte de ce qui est posé et pas d'une
 * grille vide.
 *
 * Tolérante par construction : « Monday 17:00-19:00 » (l'aller-retour demande)
 * se lit aussi (parseInt s'arrête au « : »), et toute forme illisible est
 * IGNORÉE plutôt que de casser la grille — le parent re-cochera ce créneau.
 *
 * Rend TOUT ce qui se lit, y compris hors grille : les cartes s'en servent pour
 * dire ce qui est posé, et un créneau posé doit se lire même si on ne le vend
 * plus. Pour amorcer une sélection MODIFIABLE, c'est
 * {@link canonicalSlotsToSelectableGridSlots} qu'il faut.
 *
 * @param {unknown} slots
 * @returns {string[]}
 */
export const canonicalSlotsToGridSlots = (slots) => {
    if (!Array.isArray(slots)) return []

    const expanded = []
    slots.forEach((slot) => {
        if (typeof slot !== "string") return
        const [day, range] = slot.split(" ")
        if (!day || !range || !range.includes("-")) return
        const [start, end] = range.split("-").map((h) => parseInt(h, 10))
        if (isNaN(start) || isNaN(end) || end <= start) return
        for (let hour = start; hour < end; hour++) {
            const pad = (h) => h.toString().padStart(2, "0")
            expanded.push(`${day} ${pad(hour)}h-${pad(hour + 1)}h`)
        }
    })

    return expanded
}

/**
 * Une sélection de grille (« Monday 17h-18h ») réduite à ce que la grille sait
 * AFFICHER — la forme à garder pour toute sélection que le parent peut modifier.
 *
 * La modale n'a pas de case pour un créneau hors grille : le parent ne peut ni
 * le voir ni le décocher, alors que le submit poste la sélection ENTIÈRE. Un
 * créneau hors grille se re-postait donc à chaque validation, invisible, et le
 * serveur le refuse désormais — la famille se retrouverait bloquée sur un refus
 * qu'aucun geste à l'écran ne lève.
 *
 * À rejouer à CHAQUE changement de formule, pas seulement à l'amorçage : le
 * dimanche disparaît de la grille en passant en cours partagés, et la case déjà
 * cochée deviendrait tout aussi insupprimable.
 *
 * @param {unknown} slots
 * @param {{ includeSunday?: boolean }} [options]
 * @returns {string[]}
 */
export const keepSelectableGridSlots = (
    slots,
    { includeSunday = false } = {}
) => {
    const offered = new Set(constraintsGridSlots({ includeSunday }))

    return (Array.isArray(slots) ? slots : []).filter((slot) =>
        offered.has(slot)
    )
}

/**
 * {@link canonicalSlotsToGridSlots} puis {@link keepSelectableGridSlots} : la
 * forme à utiliser pour amorcer une sélection modifiable depuis les créneaux
 * canoniques du serveur.
 *
 * @param {unknown} slots
 * @param {{ includeSunday?: boolean }} [options]
 * @returns {string[]}
 */
export const canonicalSlotsToSelectableGridSlots = (
    slots,
    { includeSunday = false } = {}
) =>
    keepSelectableGridSlots(canonicalSlotsToGridSlots(slots), { includeSunday })
